/**
 * 1 Card validation – The 1 card number via Mechanic webhooks.
 *
 * After verify: poll section via Section Rendering API, then one clean reload.
 * Linked state comes from customer.metafields.one_card.number.
 */
(function () {
  'use strict';

  var FORM_SELECTOR = '[data-jsc-one-card-form]';
  var AWAIT_KEY = 'jsc_oc_awaiting_result';
  var ATTEMPT_KEY = 'jsc_oc_await_attempts';
  var SEEN_EMPTY_KEY = 'jsc_oc_seen_empty';
  var BASELINE_KEY = 'jsc_oc_baseline';
  var MAX_ATTEMPTS = 12;
  var CARD_PATTERN = /^[0-9]{8,20}$/;

  var DEFAULT_MESSAGES = {
    missing_input: 'Please enter your 1 Card number.',
    generic: 'Something went wrong. Please try again.',
    success: 'Your 1 Card has been linked successfully.',
    removed: 'Your 1 Card has been removed.',
    pending: 'Verifying…',
    overlay: 'Verifying your 1 Card. Please wait…',
    overlay_remove: 'Removing your 1 Card. Please wait…',
    pending_result: 'Verification is still processing. Please try again in a moment.'
  };

  function digitsOnly(value) {
    return String(value == null ? '' : value).replace(/\D/g, '');
  }

  function readJson(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function resultSignature(status, errorMessage, linked) {
    return String(status || '') + '|' + String(errorMessage || '') + '|' + String(linked ? '1' : '0');
  }

  function readMessages(form) {
    var messages = {};
    var key;
    var parsed = readJson(form.querySelector('[data-jsc-oc-messages]'));

    for (key in DEFAULT_MESSAGES) {
      if (Object.prototype.hasOwnProperty.call(DEFAULT_MESSAGES, key)) {
        messages[key] = DEFAULT_MESSAGES[key];
      }
    }

    if (!parsed) return messages;

    for (key in parsed) {
      if (Object.prototype.hasOwnProperty.call(parsed, key) && parsed[key]) {
        messages[key] = parsed[key];
      }
    }

    return messages;
  }

  function OneCardValidationForm(form) {
    this.form = form;
    this.cardInput = form.querySelector('[data-jsc-oc-card]');
    this.submitButton = form.querySelector('[data-jsc-oc-submit]');
    this.submitLabel = form.querySelector('[data-jsc-oc-submit-label]');
    this.removeButton = form.querySelector('[data-jsc-oc-remove]');
    this.errorNotice = form.querySelector('[data-jsc-oc-status="error"]');
    this.successNotice = form.querySelector('[data-jsc-oc-status="success"]');
    this.clearTagsInput = form.querySelector('[data-jsc-oc-clear-tags]');
    this.overlay = document.querySelector('[data-jsc-oc-overlay]');
    this.overlayText = document.querySelector('[data-jsc-oc-overlay-text]');
    this.endpoint = form.dataset.endpoint;
    this.deleteEndpoint = form.dataset.deleteEndpoint;
    this.removeErrorEndpoint = form.dataset.removeErrorEndpoint;
    this.sectionId = form.dataset.sectionId || '';
    this.pollDelay = parseInt(form.dataset.pollDelay, 10) || 5000;
    this.requestTime = form.dataset.requestTime || '';
    this.messages = readMessages(form);
    this.result = readJson(form.querySelector('[data-jsc-oc-result]')) || {};
    this.idleLabel = this.submitLabel ? this.submitLabel.textContent : '';
    this.isSubmitting = false;
    this.pollTimer = null;

    this.bind();
    this.clearResultTags();
    this.applyPendingResult();
  }

  OneCardValidationForm.prototype.bind = function () {
    var self = this;

    if (this.cardInput) {
      this.cardInput.addEventListener('input', function () {
        self.sanitizeCard(self.cardInput);
        self.clearNotices();
      });

      this.cardInput.addEventListener('paste', function (event) {
        event.preventDefault();
        var clipboard = event.clipboardData || window.clipboardData;
        var pasted = clipboard ? clipboard.getData('text') : '';
        var maxLength = parseInt(self.cardInput.getAttribute('maxlength'), 10) || 20;
        self.cardInput.value = digitsOnly(pasted).slice(0, maxLength);
        self.clearNotices();
      });
    }

    this.form.addEventListener('submit', function (event) {
      event.preventDefault();
      self.submit();
    });

    if (this.removeButton) {
      this.removeButton.addEventListener('click', function (event) {
        event.preventDefault();
        self.removeCard();
      });
    }
  };

  OneCardValidationForm.prototype.sanitizeCard = function (input) {
    var caret = input.selectionStart;
    var original = input.value;
    var cleaned = digitsOnly(original);

    if (cleaned === original) return;

    input.value = cleaned;

    if (caret !== null && typeof input.setSelectionRange === 'function') {
      var offset = Math.max(caret - (original.length - cleaned.length), 0);
      input.setSelectionRange(offset, offset);
    }
  };

  OneCardValidationForm.prototype.showNotice = function (notice, message) {
    if (!notice) return;
    var text = notice.querySelector('[data-jsc-oc-status-text]');
    if (text) text.textContent = message;
    notice.hidden = false;
  };

  OneCardValidationForm.prototype.clearNotices = function () {
    if (this.errorNotice) this.errorNotice.hidden = true;
    if (this.successNotice) this.successNotice.hidden = true;
    if (this.cardInput) this.cardInput.classList.remove('invalid');
  };

  OneCardValidationForm.prototype.setPending = function (isPending) {
    this.isSubmitting = isPending;
    if (this.submitButton && this.form.dataset.linked !== 'true') {
      this.submitButton.disabled = isPending;
    }
    if (this.removeButton) this.removeButton.disabled = isPending;
    if (this.submitLabel && this.form.dataset.linked !== 'true') {
      this.submitLabel.textContent = isPending ? this.messages.pending : this.idleLabel;
    }
  };

  OneCardValidationForm.prototype.showOverlay = function (message) {
    if (this.overlayText) this.overlayText.textContent = message || this.messages.overlay;
    if (this.overlay) {
      this.overlay.hidden = false;
      document.documentElement.classList.add('jsc-oc-overlay-open');
    }
  };

  OneCardValidationForm.prototype.hideOverlay = function () {
    if (this.overlay) this.overlay.hidden = true;
    document.documentElement.classList.remove('jsc-oc-overlay-open');
  };

  OneCardValidationForm.prototype.clearAwaitState = function () {
    if (this.pollTimer) {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }

    try {
      window.sessionStorage.removeItem(AWAIT_KEY);
      window.sessionStorage.removeItem(ATTEMPT_KEY);
      window.sessionStorage.removeItem(SEEN_EMPTY_KEY);
      window.sessionStorage.removeItem(BASELINE_KEY);
    } catch (error) {
      // Ignore storage errors.
    }
  };

  OneCardValidationForm.prototype.getAwaitAttempts = function () {
    try {
      return parseInt(window.sessionStorage.getItem(ATTEMPT_KEY), 10) || 0;
    } catch (error) {
      return 0;
    }
  };

  OneCardValidationForm.prototype.setAwaitAttempts = function (count) {
    try {
      window.sessionStorage.setItem(ATTEMPT_KEY, String(count));
    } catch (error) {
      // Ignore storage errors.
    }
  };

  OneCardValidationForm.prototype.hasSeenEmpty = function () {
    try {
      return window.sessionStorage.getItem(SEEN_EMPTY_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  OneCardValidationForm.prototype.markSeenEmpty = function () {
    try {
      window.sessionStorage.setItem(SEEN_EMPTY_KEY, '1');
    } catch (error) {
      // Ignore storage errors.
    }
  };

  OneCardValidationForm.prototype.getBaseline = function () {
    try {
      return window.sessionStorage.getItem(BASELINE_KEY) || '';
    } catch (error) {
      return '';
    }
  };

  OneCardValidationForm.prototype.getStatus = function () {
    return this.form.dataset.verifyStatus || this.result.status || '';
  };

  OneCardValidationForm.prototype.getErrorMessage = function () {
    return this.result.error_message || '';
  };

  OneCardValidationForm.prototype.isLinked = function () {
    return this.form.dataset.linked === 'true' || this.result.linked === true;
  };

  OneCardValidationForm.prototype.finishAwaiting = function (fallbackErrorMessage) {
    var status = this.getStatus();

    this.clearAwaitState();
    this.hideOverlay();
    this.setPending(false);

    if (status === 'success' || status === 'error' || this.isLinked()) return;

    this.clearNotices();
    this.showNotice(this.errorNotice, fallbackErrorMessage || this.messages.pending_result);
  };

  OneCardValidationForm.prototype.fetchSectionHtml = function () {
    if (!this.sectionId) {
      return Promise.reject(new Error('missing section id'));
    }

    var url =
      window.location.pathname +
      '?section_id=' +
      encodeURIComponent(this.sectionId) +
      '&_ts=' +
      Date.now();

    return fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html'
      }
    }).then(function (response) {
      if (!response.ok) {
        var error = new Error('section fetch failed');
        error.status = response.status;
        throw error;
      }
      return response.text();
    });
  };

  OneCardValidationForm.prototype.parseStatusFromHtml = function (html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var form = doc.querySelector(FORM_SELECTOR);

    if (!form) {
      return {
        status: '',
        errorMessage: '',
        linked: false
      };
    }

    var result = readJson(form.querySelector('[data-jsc-oc-result]')) || {};

    return {
      status: form.dataset.verifyStatus || result.status || '',
      errorMessage: result.error_message || '',
      linked: form.dataset.linked === 'true' || result.linked === true
    };
  };

  OneCardValidationForm.prototype.schedulePoll = function () {
    var self = this;

    this.showOverlay(this.messages.overlay);
    this.setPending(true);

    if (this.pollTimer) {
      window.clearTimeout(this.pollTimer);
    }

    this.pollTimer = window.setTimeout(function () {
      self.pollTimer = null;
      self.pollVerification();
    }, self.pollDelay);
  };

  OneCardValidationForm.prototype.evaluatePollResult = function (parsed) {
    var status = parsed.status || '';
    var signature = resultSignature(status, parsed.errorMessage, parsed.linked);
    var baseline = this.getBaseline();
    var attempts = this.getAwaitAttempts() + 1;
    this.setAwaitAttempts(attempts);

    if (status !== 'success' && status !== 'error' && !parsed.linked) {
      this.markSeenEmpty();
      if (attempts >= MAX_ATTEMPTS) {
        this.finishAwaiting(this.messages.pending_result);
        return;
      }
      this.schedulePoll();
      return;
    }

    var isFreshResult =
      this.hasSeenEmpty() ||
      signature !== baseline ||
      parsed.linked === true ||
      attempts >= 2;

    if (!isFreshResult) {
      if (attempts >= MAX_ATTEMPTS) {
        this.finishAwaiting(this.messages.pending_result);
        return;
      }
      this.schedulePoll();
      return;
    }

    this.clearAwaitState();
    window.location.reload();
  };

  OneCardValidationForm.prototype.pollVerification = function () {
    var self = this;

    this.fetchSectionHtml()
      .then(function (html) {
        self.evaluatePollResult(self.parseStatusFromHtml(html));
      })
      .catch(function () {
        var attempts = self.getAwaitAttempts() + 1;
        self.setAwaitAttempts(attempts);

        if (attempts >= MAX_ATTEMPTS) {
          self.finishAwaiting(self.messages.pending_result);
          return;
        }

        self.schedulePoll();
      });
  };

  OneCardValidationForm.prototype.applyPendingResult = function () {
    var awaiting = false;

    try {
      awaiting = window.sessionStorage.getItem(AWAIT_KEY) === '1';
    } catch (error) {
      awaiting = false;
    }

    if (!awaiting) return;

    this.schedulePoll();
  };

  OneCardValidationForm.prototype.clearResultTags = function () {
    if (!this.clearTagsInput || !this.removeErrorEndpoint) return;

    var tag = this.clearTagsInput.value;
    var customerId = this.clearTagsInput.getAttribute('data-customer-id') || this.form.dataset.customerId;

    if (!tag || !customerId) return;

    fetch(this.removeErrorEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'omit',
      body: JSON.stringify({
        tag: tag,
        customer_id: customerId
      })
    }).catch(function () {
      // Best-effort tag cleanup.
    });
  };

  OneCardValidationForm.prototype.validate = function () {
    var card = this.cardInput ? digitsOnly(this.cardInput.value) : '';

    if (!card) {
      if (this.cardInput) {
        this.cardInput.classList.add('invalid');
        this.cardInput.focus();
      }
      this.showNotice(this.errorNotice, this.messages.missing_input);
      return null;
    }

    if (!CARD_PATTERN.test(card)) {
      if (this.cardInput) {
        this.cardInput.classList.add('invalid');
        this.cardInput.focus();
      }
      this.showNotice(this.errorNotice, this.messages.missing_input);
      return null;
    }

    return {
      sourceTransID: 'CMG_' + (this.form.dataset.customerId || '') + '_' + this.requestTime,
      requestTime: this.requestTime,
      cardNo: card,
      customerId: this.form.dataset.customerId || ''
    };
  };

  OneCardValidationForm.prototype.beginVerificationWait = function () {
    try {
      window.sessionStorage.setItem(AWAIT_KEY, '1');
      window.sessionStorage.setItem(ATTEMPT_KEY, '0');
      window.sessionStorage.setItem(SEEN_EMPTY_KEY, '0');
      window.sessionStorage.setItem(
        BASELINE_KEY,
        resultSignature(this.getStatus(), this.getErrorMessage(), this.isLinked())
      );
    } catch (error) {
      // Polling still runs.
    }

    this.schedulePoll();
  };

  OneCardValidationForm.prototype.submit = function () {
    if (this.isSubmitting) return;
    if (this.form.dataset.linked === 'true') return;

    this.clearNotices();

    var payload = this.validate();
    if (!payload) return;

    if (!this.endpoint) {
      this.showNotice(this.errorNotice, this.messages.generic);
      return;
    }

    var self = this;
    this.setPending(true);
    this.showOverlay(this.messages.overlay);

    fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'omit',
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (response.status === 204 || response.status === 200) {
          return {};
        }

        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (body) {
            if (!response.ok || body.ok === false) {
              var error = new Error(body.message || self.messages.generic);
              error.status = response.status;
              throw error;
            }
            return body;
          });
      })
      .then(function () {
        self.beginVerificationWait();
      })
      .catch(function (error) {
        self.clearAwaitState();
        self.hideOverlay();
        self.setPending(false);
        self.showNotice(self.errorNotice, error.message || self.messages.generic);
      });
  };

  OneCardValidationForm.prototype.removeCard = function () {
    if (this.isSubmitting) return;
    if (!this.deleteEndpoint) {
      this.showNotice(this.errorNotice, this.messages.generic);
      return;
    }

    var self = this;
    this.clearNotices();
    this.setPending(true);
    this.showOverlay(this.messages.overlay_remove);

    fetch(this.deleteEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'omit',
      body: JSON.stringify({
        customerId: this.form.dataset.customerId || ''
      })
    })
      .then(function (response) {
        if (response.status === 204 || response.status === 200 || response.ok) {
          return;
        }
        var error = new Error(self.messages.generic);
        error.status = response.status;
        throw error;
      })
      .then(function () {
        window.setTimeout(function () {
          window.location.reload();
        }, 1500);
      })
      .catch(function (error) {
        self.hideOverlay();
        self.setPending(false);
        self.showNotice(self.errorNotice, error.message || self.messages.generic);
      });
  };

  function init(root) {
    var scope = root || document;
    var forms = scope.querySelectorAll(FORM_SELECTOR);

    Array.prototype.forEach.call(forms, function (form) {
      if (form.dataset.jscOcBound === 'true') return;
      form.dataset.jscOcBound = 'true';
      new OneCardValidationForm(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
